import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiEye, FiSave, FiUpload } from 'react-icons/fi';
import SEO from '../SEO/SEO';
import RichTextEditor from './RichTextEditor';
import SeoPreview from './SeoPreview';
import { adminAPI, getMediaUrl } from '../../services/blogApi';
import { BlogCreatePayload, BlogStatus } from '../../types';
import { BLOG_CATEGORIES } from '../../constants/blogCategories';

const emptyForm: BlogCreatePayload = {
  title: '',
  slug: '',
  content: '',
  excerpt: '',
  feature_image_url: '',
  feature_image_alt: '',
  primary_keyword: '',
  secondary_keywords: [],
  meta_title: '',
  meta_description: '',
  tags: [],
  category: 'General',
  status: 'draft',
};

type EditorTab = 'edit' | 'preview';

type FieldErrors = Partial<Record<'title' | 'content' | 'excerpt' | 'primary_keyword' | 'meta_title' | 'meta_description', string>>;

const stripHtml = (html: string) =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const isContentEmpty = (html: string) => stripHtml(html).length === 0;

const AdminBlogEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState<BlogCreatePayload>(emptyForm);
  const [secondaryKeywordsInput, setSecondaryKeywordsInput] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [contentTab, setContentTab] = useState<EditorTab>('edit');

  useEffect(() => {
    if (!isEdit || !id) return;

    const loadBlog = async () => {
      try {
        setLoading(true);
        const blog = await adminAPI.getBlog(id);
        setForm({
          title: blog.title,
          slug: blog.slug,
          content: blog.content,
          excerpt: blog.excerpt,
          feature_image_url: blog.feature_image_url || '',
          feature_image_alt: blog.feature_image_alt || '',
          primary_keyword: blog.primary_keyword,
          secondary_keywords: blog.secondary_keywords || [],
          meta_title: blog.meta_title,
          meta_description: blog.meta_description,
          tags: blog.tags || [],
          category: blog.category || 'General',
          status: blog.status,
        });
        setSecondaryKeywordsInput((blog.secondary_keywords || []).join(', '));
        setTagsInput((blog.tags || []).join(', '));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load blog');
      } finally {
        setLoading(false);
      }
    };

    loadBlog();
  }, [id, isEdit]);

  const updateField = (field: keyof BlogCreatePayload, value: string | BlogStatus) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field in fieldErrors) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field as keyof FieldErrors];
        return next;
      });
    }
  };

  const validateForm = (): FieldErrors => {
    const errors: FieldErrors = {};
    const title = form.title.trim();

    if (!title) {
      errors.title = 'Title is required.';
    } else if (title.length < 3) {
      errors.title = 'Title must be at least 3 characters.';
    }

    if (isContentEmpty(form.content)) {
      errors.content = 'Article content is required.';
    } else if (stripHtml(form.content).length < 50) {
      errors.content = 'Article content must be at least 50 characters.';
    }

    if (!form.excerpt.trim()) {
      errors.excerpt = 'Excerpt is required.';
    } else if (form.excerpt.trim().length < 20) {
      errors.excerpt = 'Excerpt must be at least 20 characters.';
    }

    if (!form.primary_keyword.trim()) {
      errors.primary_keyword = 'Primary keyword is required.';
    }

    if (!form.meta_title.trim()) {
      errors.meta_title = 'Meta title is required.';
    } else if (form.meta_title.trim().length < 10) {
      errors.meta_title = 'Meta title must be at least 10 characters.';
    }

    if (!form.meta_description.trim()) {
      errors.meta_description = 'Meta description is required.';
    } else if (form.meta_description.trim().length < 50) {
      errors.meta_description = 'Meta description must be at least 50 characters.';
    }

    setFieldErrors(errors);
    return errors;
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const result = await adminAPI.uploadImage(file);
      updateField('feature_image_url', result.url);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const parseList = (value: string) =>
    value.split(',').map((item) => item.trim()).filter(Boolean);

  const handleSubmit = async (event: React.FormEvent, status?: BlogStatus) => {
    event.preventDefault();
    setError('');

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      if (errors.content) {
        setContentTab('edit');
      }
      return;
    }

    setSaving(true);

    const payload: BlogCreatePayload = {
      ...form,
      secondary_keywords: parseList(secondaryKeywordsInput),
      tags: parseList(tagsInput),
      status: status || form.status,
    };

    try {
      if (isEdit && id) {
        await adminAPI.updateBlog(id, payload);
      } else {
        await adminAPI.createBlog(payload);
      }
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-16 text-center text-gray-500">Loading article...</div>;
  }

  return (
    <>
      <SEO
        title={isEdit ? 'Edit Blog' : 'New Blog'}
        description="Blog editor"
        url={isEdit ? `/admin/blogs/edit/${id}` : '/admin/blogs/new'}
      />
      <div className="max-w-4xl mx-auto py-8 px-4">
        <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-blue-600 mb-6">
          <FiArrowLeft /> Back to dashboard
        </Link>

        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
          {isEdit ? 'Edit Article' : 'Create New Article'}
        </h1>

        <form onSubmit={(e) => handleSubmit(e)} className="space-y-6">
          <section className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Article Content</h2>

            <input
              type="text"
              placeholder="Title"
              value={form.title}
              onChange={(e) => updateField('title', e.target.value)}
              className={`w-full px-4 py-3 rounded-xl border bg-white dark:bg-slate-900 ${
                fieldErrors.title
                  ? 'border-red-500 dark:border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              aria-invalid={Boolean(fieldErrors.title)}
            />
            {fieldErrors.title && <p className="text-sm text-red-600">{fieldErrors.title}</p>}

            <input
              type="text"
              placeholder="Slug (optional, auto-generated from title)"
              value={form.slug}
              onChange={(e) => updateField('slug', e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-900"
            />

            <select
              value={form.category}
              onChange={(e) => updateField('category', e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-900"
            >
              {BLOG_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>

            <textarea
              placeholder="Excerpt (short summary for listing pages)"
              rows={3}
              value={form.excerpt}
              onChange={(e) => updateField('excerpt', e.target.value)}
              className={`w-full px-4 py-3 rounded-xl border bg-white dark:bg-slate-900 ${
                fieldErrors.excerpt
                  ? 'border-red-500 dark:border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              aria-invalid={Boolean(fieldErrors.excerpt)}
            />
            {fieldErrors.excerpt && <p className="text-sm text-red-600">{fieldErrors.excerpt}</p>}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setContentTab('edit')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                  contentTab === 'edit'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200'
                }`}
              >
                Editor
              </button>
              <button
                type="button"
                onClick={() => setContentTab('preview')}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold ${
                  contentTab === 'preview'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200'
                }`}
              >
                <FiEye size={14} /> Preview
              </button>
            </div>

            {contentTab === 'edit' ? (
              <div>
                <RichTextEditor value={form.content} onChange={(value) => updateField('content', value)} />
                {fieldErrors.content && <p className="text-sm text-red-600 mt-2">{fieldErrors.content}</p>}
              </div>
            ) : (
              <div className="rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-900 p-6 min-h-[280px]">
                {form.content ? (
                  <article
                    className="blog-preview-content text-gray-800 dark:text-gray-100"
                    dangerouslySetInnerHTML={{ __html: form.content }}
                  />
                ) : (
                  <p className="text-gray-400 text-sm">Nothing to preview yet. Write some content in the editor.</p>
                )}
              </div>
            )}
          </section>

          <section className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Feature Image</h2>

            <div className="flex flex-wrap items-center gap-4">
              <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white cursor-pointer">
                <FiUpload />
                {uploading ? 'Uploading...' : 'Upload Image'}
                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              </label>
              {form.feature_image_url && (
                <img
                  src={getMediaUrl(form.feature_image_url)}
                  alt="Preview"
                  className="h-20 w-32 object-cover rounded-lg border"
                />
              )}
            </div>

            <input
              type="text"
              placeholder="Image alt text (SEO)"
              value={form.feature_image_alt}
              onChange={(e) => updateField('feature_image_alt', e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-900"
            />
          </section>

          <section className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">SEO Settings</h2>

            <input
              type="text"
              placeholder="Primary keyword (required)"
              value={form.primary_keyword}
              onChange={(e) => updateField('primary_keyword', e.target.value)}
              className={`w-full px-4 py-3 rounded-xl border bg-white dark:bg-slate-900 ${
                fieldErrors.primary_keyword
                  ? 'border-red-500 dark:border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              aria-invalid={Boolean(fieldErrors.primary_keyword)}
            />
            {fieldErrors.primary_keyword && (
              <p className="text-sm text-red-600">{fieldErrors.primary_keyword}</p>
            )}

            <input
              type="text"
              placeholder="Secondary keywords (comma separated)"
              value={secondaryKeywordsInput}
              onChange={(e) => setSecondaryKeywordsInput(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-900"
            />

            <input
              type="text"
              placeholder="Meta title (50-60 chars recommended)"
              maxLength={70}
              value={form.meta_title}
              onChange={(e) => updateField('meta_title', e.target.value)}
              className={`w-full px-4 py-3 rounded-xl border bg-white dark:bg-slate-900 ${
                fieldErrors.meta_title
                  ? 'border-red-500 dark:border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              aria-invalid={Boolean(fieldErrors.meta_title)}
            />
            {fieldErrors.meta_title && <p className="text-sm text-red-600">{fieldErrors.meta_title}</p>}

            <textarea
              placeholder="Meta description (140-160 chars recommended)"
              maxLength={160}
              rows={3}
              value={form.meta_description}
              onChange={(e) => updateField('meta_description', e.target.value)}
              className={`w-full px-4 py-3 rounded-xl border bg-white dark:bg-slate-900 ${
                fieldErrors.meta_description
                  ? 'border-red-500 dark:border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              aria-invalid={Boolean(fieldErrors.meta_description)}
            />
            {fieldErrors.meta_description && (
              <p className="text-sm text-red-600">{fieldErrors.meta_description}</p>
            )}

            <SeoPreview
              title={form.meta_title}
              description={form.meta_description}
              slug={form.slug || form.title}
            />

            <input
              type="text"
              placeholder="Tags (comma separated)"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-900"
            />
          </section>

          {error && (
            <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600">{error}</div>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              disabled={saving}
              onClick={(e) => handleSubmit(e, 'draft')}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-gray-300 dark:border-gray-600 font-semibold"
            >
              <FiSave /> Save Draft
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={(e) => handleSubmit(e, 'published')}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold"
            >
              {saving ? 'Saving...' : 'Publish'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AdminBlogEditor;
